import React, { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Star,
  Sparkles,
  TrendingUp,
  Users,
  Zap,
  Mail,
  Link2,
  MessageCircle,
  CheckCircle,
  Trophy,
  Target,
  Globe,
  Heart,
  Camera,
  Video,
  Youtube,
  Utensils,
  Calendar,
  MapPin,
  ShoppingBag,
  Palette,
  Share2,
  Briefcase,
  Clock,
  UserCheck,
  Layout,
  Edit3,
  Rocket,
  Smartphone,
  BarChart3,
  X,
  Navigation,
  MapPin as MapIcon,
  Bell,
  Battery,
} from "lucide-react";
import Footer from "./Footer";
import Header from "./Header";
import WhatsAppFloat from "./WhatsAppFloat";
import { useNavigate, useLocation } from "react-router-dom";
import { url } from "./constant";

export default function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [stats, setStats] = useState({
    families: 0,
    countries: 0,
    locationChecks: 0
  });

  // Animated counter effect
  useEffect(() => {
    const animateValue = (key, start, end, duration) => {
      const range = end - start;
      const increment = range / (duration / 16);
      let current = start;
      
      const timer = setInterval(() => {
        current += increment;
        if (current >= end) {
          current = end;
          clearInterval(timer);
        }
        setStats(prev => ({...prev, [key]: Math.floor(current)}));
      }, 16);
    };

    const timeout = setTimeout(() => {
      animateValue('families', 0, 50000, 2000);
      animateValue('countries', 0, 195, 2000);
      animateValue('locationChecks', 0, 1000000, 2500);
    }, 500);

    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    // Intersection Observer for scroll animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px",
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("animate-in");
        }
      });
    }, observerOptions);

    // Observe all animated elements
    const animatedElements = document.querySelectorAll(".scroll-animate");
    animatedElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prepare mailto link with form data
    const subject = encodeURIComponent(`TrackFam Contact: Message from ${name.trim()}`);
    const body = encodeURIComponent(
      `Name: ${name.trim()}\n` +
      `Email: ${email.trim()}\n\n` +
      `Message:\n${message.trim()}`
    );
    
    // Open desktop mail client
    window.location.href = `mailto:support@trackfam.app?subject=${subject}&body=${body}`;
    
    // Show success message and reset form
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
      setName("");
      setEmail("");
      setMessage("");
    }, 800);
  };

  // Auto-hide success after 3 seconds
  useEffect(() => {
    if (submitted) {
      const timer = setTimeout(() => setSubmitted(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [submitted]);

  // Scroll to contact when URL has #contact
  useEffect(() => {
    if (location.hash === '#contact') {
      const el = document.getElementById('contact');
      if (el) {
        const y = el.getBoundingClientRect().top + window.pageYOffset - 100;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      {/* Hero Section */}
      <section className="relative min-h-[70vh] md:min-h-[80vh] pt-20 md:pt-24 flex items-center overflow-hidden bg-gradient-to-br from-white via-purple-50/30 to-white">
        <div className="relative w-full flex flex-col md:flex-row items-center md:items-center justify-center md:justify-between gap-4 md:gap-4 px-4 sm:px-6 md:px-8">
          {/* Left Section - Text and Button */}
          <div className="flex-[1.7] md:max-w-4xl max-w-3xl text-center md:text-left">
            {/* Trust Badge */}
            <div className="inline-flex items-center gap-2 bg-white rounded-full px-4 py-2 border-2 border-black shadow-[2px_2px_0_#000] mb-6">
              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
              <span className="text-sm font-semibold text-black ml-1">Trusted by 50,000+ families</span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-black mb-6 md:mb-8 leading-tight text-left">
              <div className="mb-2">Keep Your Family</div>
              <div className="bg-gradient-to-r from-[#62329f] to-purple-600 bg-clip-text text-transparent">Safe & Connected</div>
            </h1>

            <p className="text-lg md:text-xl text-gray-700 mb-8 md:mb-12 leading-relaxed text-left max-w-2xl">
              Know where your loved ones are at all times. Real-time location tracking, instant place alerts, and battery monitoring — all in one secure app for your peace of mind.
            </p>

            {/* App Download Links */}
            <div className="flex flex-row items-start gap-3 sm:gap-4">
              <a
                href="https://apps.apple.com/ae/app/trackfam/id6752317391" 
                target="_blank"
                rel="noreferrer"
                aria-label="Download TrackFam on the App Store"
                className="inline-flex transition-transform hover:-translate-y-1 hover:scale-105"
              >
                <img
                  src="/Image/logo_appstore.svg"
                  alt="Download TrackFam on the App Store"
                  className="h-12 md:h-14 w-auto object-contain"
                  loading="eager"
                />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.washey.familytrack"
                target="_blank"
                rel="noreferrer"
                aria-label="Get TrackFam on Google Play"
                className="inline-flex transition-transform hover:-translate-y-1 hover:scale-105"
              >
                <img
                  src="/Image/logo_playstore.svg"
                  alt="Get TrackFam on Google Play"
                  className="h-12 md:h-14 w-auto object-contain"
                  loading="eager"
                />
              </a>
            </div>
          </div>

          {/* Right Section - App Preview */}
          <div className="flex-1 relative flex justify-center md:justify-end mt-8 md:mt-0">
            <div className="relative w-full max-w-[400px] md:max-w-[600px] animate-float">
              <img
                src="/TrackFam/locationpermission(always)-trackfambg.png"
                alt="TrackFam app showing family location tracking on mobile device"
                className="w-full h-auto object-contain drop-shadow-2xl"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Trust Statistics Section */}
      <section className="relative bg-gradient-to-br from-[#62329f] to-purple-700 py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Trusted Worldwide by Families
            </h2>
            <p className="text-purple-100 text-lg">
              Join thousands of families who trust TrackFam for their safety
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Stat 1 */}
            <div className="relative scroll-animate">
              <div className="absolute inset-0 translate-x-2 translate-y-2 bg-black/30 rounded-2xl"></div>
              <div className="relative bg-white rounded-2xl border-4 border-black p-8 text-center transition-transform hover:-translate-x-1 hover:-translate-y-1">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#62329f]/10 border-2 border-[#62329f] flex items-center justify-center">
                  <Users className="w-8 h-8 text-[#62329f]" />
                </div>
                <div className="text-4xl md:text-5xl font-bold text-black mb-2">
                  {stats.families.toLocaleString()}+
                </div>
                <div className="text-gray-600 font-semibold">
                  Families Protected
                </div>
              </div>
            </div>

            {/* Stat 2 */}
            <div className="relative scroll-animate">
              <div className="absolute inset-0 translate-x-2 translate-y-2 bg-black/30 rounded-2xl"></div>
              <div className="relative bg-white rounded-2xl border-4 border-black p-8 text-center transition-transform hover:-translate-x-1 hover:-translate-y-1">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#62329f]/10 border-2 border-[#62329f] flex items-center justify-center">
                  <Globe className="w-8 h-8 text-[#62329f]" />
                </div>
                <div className="text-4xl md:text-5xl font-bold text-black mb-2">
                  {stats.countries}+
                </div>
                <div className="text-gray-600 font-semibold">
                  Countries Worldwide
                </div>
              </div>
            </div>

            {/* Stat 3 */}
            <div className="relative scroll-animate">
              <div className="absolute inset-0 translate-x-2 translate-y-2 bg-black/30 rounded-2xl"></div>
              <div className="relative bg-white rounded-2xl border-4 border-black p-8 text-center transition-transform hover:-translate-x-1 hover:-translate-y-1">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#62329f]/10 border-2 border-[#62329f] flex items-center justify-center">
                  <MapPin className="w-8 h-8 text-[#62329f]" />
                </div>
                <div className="text-4xl md:text-5xl font-bold text-black mb-2">
                  {(stats.locationChecks / 1000000).toFixed(1)}M+
                </div>
                <div className="text-gray-600 font-semibold">
                  Location Checks Daily
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative bg-white py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center scroll-animate mb-12">
            <div className="inline-flex items-center justify-center rounded-full bg-[#62329f] text-white font-semibold px-6 py-3 border-2 border-black shadow-[4px_4px_0_#000] mb-4">
              Powerful Features
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
              Everything You Need to Keep Your Family Safe
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Advanced location tracking technology designed with families in mind. Simple, secure, and reliable.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature 1: Live Location */}
            <div className="relative scroll-animate">
              <div className="absolute inset-0 translate-x-2 translate-y-2 bg-[#62329f]/20 rounded-2xl -z-10"></div>
              <div className="bg-white rounded-2xl border-4 border-black overflow-hidden h-full transition-transform duration-300 hover:-translate-x-1 hover:-translate-y-1">
                {/* Image Section */}
                <div className="relative bg-gradient-to-br from-purple-50 to-purple-100 p-6 flex items-center justify-center h-[360px]">
                  <img
                    src="/TrackFam/livelocation.png?v=2"
                    alt="Live Location Tracking"
                    className="max-w-[280px] max-h-[320px] w-auto h-auto object-contain"
                    loading="lazy"
                  />
                </div>
                
                {/* Content Section */}
                <div className="p-6 md:p-8">
                  <div className="w-14 h-14 rounded-full bg-[#62329f] border-4 border-black flex items-center justify-center mb-6">
                    <img 
                      src="/LocationIcons/tracking.png" 
                      alt="Live Location" 
                      className="w-8 h-8 object-contain"
                    />
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-black mb-4">
                    Live Location
                  </h3>
                  <p className="text-gray-700 text-sm md:text-base mb-4">
                    Track your child's location history
                  </p>
                  <ul className="space-y-2 text-sm md:text-base text-gray-600">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-[#62329f] mr-2 flex-shrink-0 mt-0.5" />
                      <span>Check their routes and ensure they're safe</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-[#62329f] mr-2 flex-shrink-0 mt-0.5" />
                      <span>Track where they were to find a lost phone</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Feature 2: Place Alert */}
            <div className="relative scroll-animate">
              <div className="absolute inset-0 translate-x-2 translate-y-2 bg-[#62329f]/20 rounded-2xl -z-10"></div>
              <div className="bg-white rounded-2xl border-4 border-black overflow-hidden h-full transition-transform duration-300 hover:-translate-x-1 hover:-translate-y-1">
                {/* Image Section */}
                <div className="relative bg-gradient-to-br from-purple-50 to-purple-100 p-6 flex items-center justify-center h-[360px]">
                  <img
                    src="/TrackFam/placealert.png"
                    alt="Place Alert Notifications"
                    className="max-w-[280px] max-h-[320px] w-auto h-auto object-contain"
                    loading="lazy"
                  />
                </div>
                
                {/* Content Section */}
                <div className="p-6 md:p-8">
                  <div className="w-14 h-14 rounded-full bg-[#62329f] border-4 border-black flex items-center justify-center mb-6">
                    <img 
                      src="/LocationIcons/tracking-app.png" 
                      alt="Place Alert" 
                      className="w-8 h-8 object-contain"
                    />
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-black mb-4">
                    Place Alert
                  </h3>
                  <p className="text-gray-700 text-sm md:text-base mb-4">
                    Get instant updates about places that your child visits
                  </p>
                  <ul className="space-y-2 text-sm md:text-base text-gray-600">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-[#62329f] mr-2 flex-shrink-0 mt-0.5" />
                      <span>Know they are where they are supposed to be</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-[#62329f] mr-2 flex-shrink-0 mt-0.5" />
                      <span>Keep an eye on safe and unsafe spots</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Feature 3: Battery Info */}
            <div className="relative scroll-animate">
              <div className="absolute inset-0 translate-x-2 translate-y-2 bg-[#62329f]/20 rounded-2xl -z-10"></div>
              <div className="bg-white rounded-2xl border-4 border-black overflow-hidden h-full transition-transform duration-300 hover:-translate-x-1 hover:-translate-y-1">
                {/* Image Section */}
                <div className="relative bg-gradient-to-br from-purple-50 to-purple-100 p-6 flex items-center justify-center h-[360px]">
                  <img
                    src="/TrackFam/battery.png"
                    alt="Battery Info Monitoring"
                    className="max-w-[280px] max-h-[320px] w-auto h-auto object-contain"
                    loading="lazy"
                  />
                </div>
                
                {/* Content Section */}
                <div className="p-6 md:p-8">
                  <div className="w-14 h-14 rounded-full bg-[#62329f] border-4 border-black flex items-center justify-center mb-6">
                    <img 
                      src="/LocationIcons/track.png" 
                      alt="Battery Info" 
                      className="w-8 h-8 object-contain"
                    />
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-black mb-4">
                    Battery Info
                  </h3>
                  <p className="text-gray-700 text-sm md:text-base mb-4">
                    Know if their battery is low
                  </p>
                  <ul className="space-y-2 text-sm md:text-base text-gray-600">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-[#62329f] mr-2 flex-shrink-0 mt-0.5" />
                      <span>Get a heads up when their batteries are low</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-[#62329f] mr-2 flex-shrink-0 mt-0.5" />
                      <span>Know when their phone is out of juice</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Promo Banner */}
      <section className="relative bg-[#62329f] py-20 md:py-15 overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-center">
          {/* Background decorative icons */}
          <div className="absolute top-4 left-4 opacity-20">
            <img 
              src="/LocationIcons/tracking.png" 
              alt="Tracking icon" 
              className="w-12 h-12 rotate-12"
            />
          </div>
          <div className="absolute top-8 right-8 opacity-20">
            <img 
              src="/LocationIcons/tracking-app.png" 
              alt="App icon" 
              className="w-16 h-16 -rotate-12"
            />
          </div>
          <div className="absolute bottom-6 left-12 opacity-20">
            <img 
              src="/LocationIcons/track.png" 
              alt="Track icon" 
              className="w-14 h-14 rotate-45"
            />
          </div>
          <div className="absolute bottom-4 right-4 opacity-20">
            <img 
              src="/LocationIcons/tracking.png" 
              alt="Tracking icon" 
              className="w-10 h-10 -rotate-45"
            />
          </div>

          <div className="relative text-center z-10">
            {/* Main icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full border-4 border-white/30 flex items-center justify-center">
                <img 
                  src="/LocationIcons/tracking-app.png" 
                  alt="Family Safety" 
                  className="w-12 h-12 object-contain"
                />
              </div>
            </div>
            
            <h2 className="text-white font-extrabold text-xl sm:text-2xl md:text-3xl mb-4">
              Peace of Mind for Parents
            </h2>
            <p className="text-white/90 mt-2 md:mt-3 text-xs sm:text-sm max-w-2xl mx-auto">
              Track your family's location in real-time, get instant alerts, and know their battery status. All in one place for your peace of mind.
            </p>

            {/* Feature highlights with icons */}
            <div className="flex flex-wrap justify-center gap-4 mt-6">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 border border-white/20">
                <img 
                  src="/LocationIcons/tracking.png" 
                  alt="Live tracking" 
                  className="w-5 h-5 object-contain"
                />
                <span className="text-white text-xs font-medium">Live Tracking</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 border border-white/20">
                <Bell className="w-5 h-5 text-white" />
                <span className="text-white text-xs font-medium">Instant Alerts</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 border border-white/20">
                <Battery className="w-5 h-5 text-white" />
                <span className="text-white text-xs font-medium">Battery Monitor</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="relative bg-white py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center scroll-animate mb-12">
            <div className="inline-flex items-center justify-center rounded-full bg-[#62329f] text-white font-semibold px-6 py-3 border-2 border-black shadow-[4px_4px_0_#000] mb-4">
              Testimonials
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
              What Families Are Saying
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Real stories from real families who trust TrackFam every day
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {/* Testimonial 1 */}
            <div className="relative scroll-animate">
              <div className="absolute inset-0 translate-x-2 translate-y-2 bg-[#62329f]/20 rounded-2xl"></div>
              <div className="relative bg-white rounded-2xl border-4 border-black p-6 md:p-8 h-full transition-transform hover:-translate-x-1 hover:-translate-y-1">
                <div className="flex gap-1 mb-4">
                  <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                </div>
                <p className="text-gray-700 mb-6 text-sm md:text-base leading-relaxed">
                  "TrackFam gives me peace of mind knowing where my kids are at all times. The battery alerts are a lifesaver!"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#62329f]/10 border-2 border-[#62329f] flex items-center justify-center">
                    <Users className="w-6 h-6 text-[#62329f]" />
                  </div>
                  <div>
                    <div className="font-bold text-black">Sarah Johnson</div>
                    <div className="text-sm text-gray-600">Mother of 3</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="relative scroll-animate">
              <div className="absolute inset-0 translate-x-2 translate-y-2 bg-[#62329f]/20 rounded-2xl"></div>
              <div className="relative bg-white rounded-2xl border-4 border-black p-6 md:p-8 h-full transition-transform hover:-translate-x-1 hover:-translate-y-1">
                <div className="flex gap-1 mb-4">
                  <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                </div>
                <p className="text-gray-700 mb-6 text-sm md:text-base leading-relaxed">
                  "The place alerts feature is amazing! I know exactly when my daughter arrives at school or soccer practice."
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#62329f]/10 border-2 border-[#62329f] flex items-center justify-center">
                    <Users className="w-6 h-6 text-[#62329f]" />
                  </div>
                  <div>
                    <div className="font-bold text-black">Michael Chen</div>
                    <div className="text-sm text-gray-600">Parent & Coach</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="relative scroll-animate">
              <div className="absolute inset-0 translate-x-2 translate-y-2 bg-[#62329f]/20 rounded-2xl"></div>
              <div className="relative bg-white rounded-2xl border-4 border-black p-6 md:p-8 h-full transition-transform hover:-translate-x-1 hover:-translate-y-1">
                <div className="flex gap-1 mb-4">
                  <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                </div>
                <p className="text-gray-700 mb-6 text-sm md:text-base leading-relaxed">
                  "Easy to use and super reliable. We feel much safer with TrackFam keeping us connected as a family."
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#62329f]/10 border-2 border-[#62329f] flex items-center justify-center">
                    <Users className="w-6 h-6 text-[#62329f]" />
                  </div>
                  <div>
                    <div className="font-bold text-black">Emily Rodriguez</div>
                    <div className="text-sm text-gray-600">Working Mom</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="relative bg-white py-16 md:py-24 overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center scroll-animate">
          <div className="inline-flex items-center justify-center rounded-full bg-[#62329f] text-white font-semibold px-6 py-3 border-2 border-black shadow-[4px_4px_0_#000] mb-4">
            How it Works
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
            Get Started in 3 Simple Steps
          </h2>
          <p className="mt-2 md:mt-4 text-gray-600 text-base md:text-lg max-w-2xl mx-auto">
            Set up TrackFam in minutes and start keeping your family safe with real-time location tracking
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-8 mt-8 md:mt-12 mb-8 md:mb-20">
            {/* Card 1 */}
            <div className="relative scroll-animate group h-full">
              <div className="absolute inset-0 translate-x-2 translate-y-2 bg-[#62329f]/30 rounded-3xl -z-10"></div>
              <div className="bg-white rounded-3xl border-4 border-black overflow-hidden transition-transform hover:-translate-x-1 hover:-translate-y-1 h-full flex flex-col">
                {/* Step Number Badge */}
                <div className="absolute top-6 left-6 z-10 w-12 h-12 rounded-full bg-[#62329f] border-3 border-black flex items-center justify-center shadow-lg">
                  <span className="text-2xl font-bold text-white">1</span>
                </div>
                
                {/* Image Section */}
                <div className="relative bg-gradient-to-br from-purple-50 to-purple-100 p-8 pt-20 flex items-center justify-center">
                  <img
                    src="/TrackFam/getstart.jpg"
                    alt="Get Started - Launch TrackFam"
                    className="w-full h-64 object-cover rounded-2xl border-3 border-black shadow-xl"
                    loading="lazy"
                  />
                </div>
                
                {/* Content Section */}
                <div className="p-8 bg-white flex-grow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-[#62329f] border-2 border-black flex items-center justify-center">
                      <UserCheck className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-black">
                      Get Started
                    </h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    Sign up with your email and create your family circle. Invite family members to join and start connecting in seconds.
                  </p>
                </div>
              </div>
            </div>

            {/* Card 2 */}
            <div className="relative scroll-animate group h-full">
              <div className="absolute inset-0 translate-x-2 translate-y-2 bg-[#62329f]/30 rounded-3xl -z-10"></div>
              <div className="bg-white rounded-3xl border-4 border-black overflow-hidden transition-transform hover:-translate-x-1 hover:-translate-y-1 h-full flex flex-col">
                {/* Step Number Badge */}
                <div className="absolute top-6 left-6 z-10 w-12 h-12 rounded-full bg-[#62329f] border-3 border-black flex items-center justify-center shadow-lg">
                  <span className="text-2xl font-bold text-white">2</span>
                </div>
                
                {/* Image Section */}
                <div className="relative bg-gradient-to-br from-purple-50 to-purple-100 p-8 pt-20 flex items-center justify-center">
                  <img
                    src="/TrackFam/downlaod.jpg"
                    alt="Download TrackFam - Mobile App Interface"
                    className="w-full h-64 object-cover rounded-2xl border-3 border-black shadow-xl"
                    loading="lazy"
                  />
                </div>
                
                {/* Content Section */}
                <div className="p-8 bg-white flex-grow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-[#62329f] border-2 border-black flex items-center justify-center">
                      <Smartphone className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-black">
                      Download TrackFam
                    </h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    Download TrackFam from App Store or Google Play. Available for both iOS and Android devices with seamless cross-platform support.
                  </p>
                </div>
              </div>
            </div>

            {/* Card 3 */}
            <div className="relative scroll-animate group h-full">
              <div className="absolute inset-0 translate-x-2 translate-y-2 bg-[#62329f]/30 rounded-3xl -z-10"></div>
              <div className="bg-white rounded-3xl border-4 border-black overflow-hidden transition-transform hover:-translate-x-1 hover:-translate-y-1 h-full flex flex-col">
                {/* Step Number Badge */}
                <div className="absolute top-6 left-6 z-10 w-12 h-12 rounded-full bg-[#62329f] border-3 border-black flex items-center justify-center shadow-lg">
                  <span className="text-2xl font-bold text-white">3</span>
                </div>
                
                {/* Image Section */}
                <div className="relative bg-gradient-to-br from-purple-50 to-purple-100 p-8 pt-20 flex items-center justify-center">
                  <img
                    src="/TrackFam/locationpermission-trackfambg.png"
                    alt="Start Tracking - Location Permission"
                    className="w-full h-64 object-contain rounded-2xl"
                    loading="lazy"
                  />
                </div>
                
                {/* Content Section */}
                <div className="p-8 bg-white flex-grow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-[#62329f] border-2 border-black flex items-center justify-center">
                      <img 
                        src="/LocationIcons/track.png" 
                        alt="Start Tracking" 
                        className="w-6 h-6 object-contain"
                      />
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-black">
                      Start Tracking
                    </h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    Get real-time location updates, set up place alerts, and monitor battery status. Your family's safety is just a tap away.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Decorative wave - bottom right */}
        <svg
          className="hidden md:block pointer-events-none select-none absolute -bottom-4 right-0 w-[648px] h-[204px]"
          viewBox="0 0 540 170"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="hiwWave" x1=" 0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#62329f" />
              <stop offset="100%" stopColor="#62329f" />
            </linearGradient>
          </defs>
          <path
            d="M0 150 C 40 120, 90 140, 130 130 C 170 75, 220 65, 260 115 C 300 155, 340 130, 380 70 C 430 0, 490 5, 540 45 L 540 170 L 0 170 Z"
            fill="url(#hiwWave)"
            stroke="#111827"
            strokeWidth="2"
          />
        </svg>
      </section>

      {/* Removed custom chat widget in favor of global WhatsApp button */}

      {/* FAQ Section */}
      <section className="relative bg-gradient-to-br from-purple-50 to-white py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center scroll-animate mb-12">
            <div className="inline-flex items-center justify-center rounded-full bg-[#62329f] text-white font-semibold px-6 py-3 border-2 border-black shadow-[4px_4px_0_#000] mb-4">
              FAQ
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-600 text-lg">
              Everything you need to know about TrackFam
            </p>
          </div>

          <div className="space-y-4">
            {/* FAQ 1 */}
            <div className="relative scroll-animate">
              <div className="absolute inset-0 translate-x-2 translate-y-2 bg-[#62329f]/20 rounded-2xl"></div>
              <div className="relative bg-white rounded-2xl border-4 border-black p-6 md:p-8 transition-transform hover:-translate-x-1 hover:-translate-y-1">
                <h3 className="text-lg md:text-xl font-bold text-black mb-3 flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#62329f] text-white flex items-center justify-center text-sm font-bold">
                    1
                  </span>
                  <span>How does TrackFam protect my family's privacy?</span>
                </h3>
                <p className="text-gray-700 ml-11">
                  Your family's privacy is our top priority. All location data is encrypted end-to-end and only visible to family members you approve. We never share or sell your data to third parties.
                </p>
              </div>
            </div>

            {/* FAQ 2 */}
            <div className="relative scroll-animate">
              <div className="absolute inset-0 translate-x-2 translate-y-2 bg-[#62329f]/20 rounded-2xl"></div>
              <div className="relative bg-white rounded-2xl border-4 border-black p-6 md:p-8 transition-transform hover:-translate-x-1 hover:-translate-y-1">
                <h3 className="text-lg md:text-xl font-bold text-black mb-3 flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#62329f] text-white flex items-center justify-center text-sm font-bold">
                    2
                  </span>
                  <span>Does TrackFam work on both iOS and Android?</span>
                </h3>
                <p className="text-gray-700 ml-11">
                  Yes! TrackFam is available on both iOS (App Store) and Android (Google Play). All features work seamlessly across both platforms, so you can track family members regardless of their device.
                </p>
              </div>
            </div>

            {/* FAQ 3 */}
            <div className="relative scroll-animate">
              <div className="absolute inset-0 translate-x-2 translate-y-2 bg-[#62329f]/20 rounded-2xl"></div>
              <div className="relative bg-white rounded-2xl border-4 border-black p-6 md:p-8 transition-transform hover:-translate-x-1 hover:-translate-y-1">
                <h3 className="text-lg md:text-xl font-bold text-black mb-3 flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#62329f] text-white flex items-center justify-center text-sm font-bold">
                    3
                  </span>
                  <span>How accurate is the location tracking?</span>
                </h3>
                <p className="text-gray-700 ml-11">
                  TrackFam uses GPS, Wi-Fi, and cellular data to provide accurate location tracking typically within 10-50 meters. Accuracy depends on signal strength and device settings.
                </p>
              </div>
            </div>

            {/* FAQ 4 */}
            <div className="relative scroll-animate">
              <div className="absolute inset-0 translate-x-2 translate-y-2 bg-[#62329f]/20 rounded-2xl"></div>
              <div className="relative bg-white rounded-2xl border-4 border-black p-6 md:p-8 transition-transform hover:-translate-x-1 hover:-translate-y-1">
                <h3 className="text-lg md:text-xl font-bold text-black mb-3 flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#62329f] text-white flex items-center justify-center text-sm font-bold">
                    4
                  </span>
                  <span>Will TrackFam drain my phone battery?</span>
                </h3>
                <p className="text-gray-700 ml-11">
                  We've optimized TrackFam to minimize battery usage. The app uses intelligent location updates that balance accuracy with battery life. Most users report minimal impact on their device's battery.
                </p>
              </div>
            </div>

            {/* FAQ 5 */}
            <div className="relative scroll-animate">
              <div className="absolute inset-0 translate-x-2 translate-y-2 bg-[#62329f]/20 rounded-2xl"></div>
              <div className="relative bg-white rounded-2xl border-4 border-black p-6 md:p-8 transition-transform hover:-translate-x-1 hover:-translate-y-1">
                <h3 className="text-lg md:text-xl font-bold text-black mb-3 flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#62329f] text-white flex items-center justify-center text-sm font-bold">
                    5
                  </span>
                  <span>Can I set up alerts for specific locations?</span>
                </h3>
                <p className="text-gray-700 ml-11">
                  Absolutely! Place Alerts let you define safe zones like home, school, or work. You'll receive instant notifications when family members arrive or leave these locations.
                </p>
              </div>
            </div>

            {/* FAQ 6 */}
            <div className="relative scroll-animate">
              <div className="absolute inset-0 translate-x-2 translate-y-2 bg-[#62329f]/20 rounded-2xl"></div>
              <div className="relative bg-white rounded-2xl border-4 border-black p-6 md:p-8 transition-transform hover:-translate-x-1 hover:-translate-y-1">
                <h3 className="text-lg md:text-xl font-bold text-black mb-3 flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#62329f] text-white flex items-center justify-center text-sm font-bold">
                    6
                  </span>
                  <span>Is there a free trial available?</span>
                </h3>
                <p className="text-gray-700 ml-11">
                  Yes! We offer a free trial so you can experience all of TrackFam's features. Download the app and get started today with no credit card required.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="relative bg-white py-12 md:py-20">
        <div className="w-full px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-start">
          {/* Left column: copy and links */}
          <div className="text-left">
          <div className="inline-flex items-center justify-center rounded-full bg-[#62329f] text-white font-semibold px-6 py-3 border-2 border-black shadow-[4px_4px_0_#000] mb-4">
              Contact Us
            </div>
            <p className="mt-4 md:mt-6 text-gray-800 text-base md:text-xl max-w-xl text-left">
              Have questions about our family tracking features? Need help setting up location sharing? 
              Contact us for support and assistance.
            </p>

            

            <div className="mt-12 md:mt-16">
              <div className="text-black font-medium mb-4">
                Get Support & Stay Connected
              </div>
              <div className="flex items-center gap-4">
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="w-10 h-10 border-2 border-black rounded-lg flex items-center justify-center hover:bg-[#34d77d]/20 transition-colors text-black"
                >
                  <img src="/instagram-logo.svg" alt="Instagram" className="w-5 h-5" />
                </a>
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="w-10 h-10 border-2 border-black rounded-lg flex items-center justify-center hover:bg-[#34d77d]/20 transition-colors text-black"
                >
                  <img src="/facebook-logo.svg" alt="Facebook" className="w-5 h-5" />
                </a>
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="w-10 h-10 border-2 border-black rounded-lg flex items-center justify-center hover:bg-[#34d77d]/20 transition-colors text-black"
                >
                  <img src="/linkedin-logo.svg" alt="LinkedIn" className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>

          {/* Right column: form */}
          <div>
            <div className="rounded-2xl border-2 border-black p-4 md:p-8 shadow-[6px_6px_0_#000]">
              {/* Success Message */}
              {submitted && (
                <div className="mb-4 md:mb-6 p-4 bg-green-100 border-2 border-green-400 text-green-700 rounded-xl">
                  <div className="flex items-center">
                    <CheckCircle className="h-5 w-5 mr-2" />
                    <span className="font-semibold">Thank you for your message! We'll get back to you soon.</span>
                  </div>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2 text-left">
                    Name<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter your name"
                    style={{ border: "2px solid #9ca3af" }}
                    className="w-full bg-white rounded-lg p-3 md:p-4 h-12 text-base text-gray-900 placeholder-gray-500 font-medium transition-all duration-200 hover:!border-gray-500 focus:outline-none focus:!border-primary-500 focus:ring-2 focus:ring-primary-500/20 hover:bg-gray-50 focus:bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2 text-left">
                    Email<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    style={{ border: "2px solid #9ca3af" }}
                    className="w-full bg-white rounded-lg p-3 md:p-4 h-12 text-base text-gray-900 placeholder-gray-500 font-medium transition-all duration-200 hover:!border-gray-500 focus:outline-none focus:!border-primary-500 focus:ring-2 focus:ring-primary-500/20 hover:bg-gray-50 focus:bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2 text-left">
                    Message<span className="text-red-500">*</span>
                  </label>
                  <textarea
                    rows="4"
                    required
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Enter your message"
                    style={{ border: "2px solid #9ca3af" }}
                    className="w-full bg-white rounded-lg p-3 md:p-4 resize-none text-gray-900 placeholder-gray-500 font-medium transition-all duration-200 hover:!border-gray-500 focus:outline-none focus:!border-primary-500 focus:ring-2 focus:ring-primary-500/20 hover:bg-gray-50 focus:bg-white"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className={`w-full rounded-xl h-12 text-base font-semibold transition-all duration-200 ${
                    loading 
                      ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
                      : 'bg-black text-white hover:bg-black/90'
                  }`}
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                      Sending...
                    </div>
                  ) : (
                    'Send Message'
                  )}
                </Button>
              </form>
              <div className="mt-4 md:mt-6 border-t border-gray-300 pt-4 md:pt-6">
                <div className="space-y-2 md:space-y-3">
                  <a
                    href="https://www.trackfam.app"
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-3 text-black hover:underline"
                  >
                    <Link2 className="w-5 h-5" />
                    <span>www.trackfam.app</span>
                  </a>
                  <a
                    href="mailto:support@trackfam.app"
                    className="flex items-center gap-3 text-black hover:underline"
                  >
                    <Mail className="w-5 h-5" />
                      <span>support@trackfam.app</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Footer */}
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
